import { createContext } from '@hover/blueprint';
import React, { useCallback, useState } from 'react';

import { swipe } from 'src/shared/telemetry';

import { useHomeownerAppContext } from '../../hooks';

import type { Style } from './styles';
import { styles } from './styles';

export const aspects = ['exterior', 'interior'] as const;
export type Aspect = (typeof aspects)[number];

/**
 * Given a potentially out-of-bounds index for the `styles` array, wrap
 * out-of-bounds values to the other end of the array.
 */
const loopStyleIndex = (index: number): number => {
  const { length } = styles;
  const wrappedIndex = index % length;

  return wrappedIndex < 0 ? wrappedIndex + length : wrappedIndex;
};

export interface HomeStylesContext {
  aspect: Aspect;
  currentStyle: Style;
  getTabId: (style: Style) => string;
  next: () => void;
  nextStyle: Style;
  previous: () => void;
  previousStyle: Style;
  setAspect: (aspect: Aspect) => void;
  setCurrentStyle: (style: Style) => void;
  styles: typeof styles;
}

const [Provider, useHomeStylesContext] = createContext<HomeStylesContext>({
  name: 'HomeStylesContext',
  strict: true,
});

export interface HomeStylesProviderProps {
  children: React.ReactNode;
  /**
   * ID prefix used for ARIA accessibility element IDs
   */
  id?: string;
}

const HomeStylesProvider = ({
  children,
  id = 'styles',
}: HomeStylesProviderProps) => {
  const [currentStyle, setCurrentStyle] = useState<Style>(styles[0]);
  const [aspect, setAspect] = useState<Aspect>('exterior');
  const { trackingData } = useHomeownerAppContext();

  const index = styles.indexOf(currentStyle);

  const nextStyle = styles[loopStyleIndex(index + 1)];
  const previousStyle = styles[loopStyleIndex(index - 1)];

  const next = useCallback(
    () => setCurrentStyle(nextStyle),
    [nextStyle, setCurrentStyle],
  );
  const previous = useCallback(
    () => setCurrentStyle(previousStyle),
    [previousStyle, setCurrentStyle],
  );

  const getTabId = useCallback(({ name }: Style) => `${id}-${name}`, [id]);

  const handleSetAspect = useCallback(
    (nextAspect: Aspect) => {
      swipe({
        ...trackingData,
        track_id: 'sample-image-switch',
      });
      setAspect(nextAspect);
    },
    [trackingData],
  );

  return (
    <Provider
      value={{
        aspect,
        currentStyle,
        getTabId,
        next,
        nextStyle,
        previous,
        previousStyle,
        setAspect: handleSetAspect,
        setCurrentStyle,
        styles,
      }}
    >
      {children}
    </Provider>
  );
};

const useHomeStyles = () => {
  const context = useHomeStylesContext();

  const {
    aspect,
    currentStyle: { exterior, interior },
  } = context;

  return {
    ...context,
    background:
      aspect === 'exterior' ? exterior.background : interior.background,
  };
};

export { HomeStylesProvider, useHomeStyles };
